import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { FaUndoAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { storeManagerAssetTransferActionApi } from "../../services/AssetService";
import { searchAreaByLocationApi } from "../../services/SearchService";
import { useSelector } from "react-redux";

const StoreAssetTransferAction = ({
  actionType,
  open,
  onClose,
  afterSubmit,
  assetData = {},
}) => {
  const [remark, setRemark] = useState("");
  const [loading, setLoading] = useState(false);
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState("");
  const [areaLoading, setAreaLoading] = useState(false);

  const isAccept = actionType === "accept";
  const userLocation = useSelector((state) => state.auth.user?.location_id);

  useEffect(() => {
    if (open) {
      setRemark("");
      setSelectedArea("");
      if (isAccept && userLocation) {
        fetchAreas(userLocation);
      }
    }
  }, [open, userLocation]);

  const fetchAreas = async (locationId) => {
    setAreaLoading(true);
    try {
      const response = await searchAreaByLocationApi(locationId);
      const areaOptions = response.data.results.map(area => ({
        value: area.value, // Full ID like are_xxx
        label: area.label,
      }));
      setAreas(areaOptions);
    } catch (error) {
      console.error("Failed to fetch areas:", error);
      toast.error("Failed to load available areas");
      setAreas([]);
    } finally {
      setAreaLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedArea) {
      toast.error("Please select a target area");
      return;
    }

    setLoading(true);
    try {
      const response = await storeManagerAssetTransferActionApi(assetData.id, {
        remarks: remark,
        action: "APPROVED",
        to_area_id: selectedArea, // <-- Correct Key Name
      });

      if (response.data?.error?.includes("missing target location")) {
        toast.error("This transfer request is invalid - missing target location");
        afterSubmit();
      } else {
        toast.success("Transfer approved successfully");
        afterSubmit();
      }
    } catch (error) {
      console.error("Approval failed:", error);
      const errorMsg = error.response?.data?.error ||
                      error.response?.data?.message ||
                      "Failed to approve transfer";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    try {
      await storeManagerAssetTransferActionApi(assetData.id, {
        remarks: remark,
        action: "REJECTED",
      });
      toast.success("Transfer rejected");
      afterSubmit();
    } catch (error) {
      console.error("Rejection failed:", error);
      toast.error(error.response?.data?.message || "Failed to reject transfer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={`d-flex align-items-center gap-2 ${isAccept ? "text-success" : "text-danger"}`}>
        <FaUndoAlt className="fs-4" />
        <span>{isAccept ? "Approve Transfer Request" : "Reject Transfer Request"}</span>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" className="mb-2">
          {isAccept
            ? "Approve this transfer request and assign to area:"
            : "Are you sure you want to reject this transfer request?"}
        </Typography>

        {isAccept && (
          <FormControl fullWidth sx={{ mb: 2 }} required>
            <InputLabel id="area-select-label">Target Area</InputLabel>
            <Select
              labelId="area-select-label"
              value={selectedArea}
              label="Target Area"
              onChange={(e) => setSelectedArea(e.target.value)}
              disabled={areaLoading}
            >
              {areaLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                  Loading areas...
                </MenuItem>
              ) : areas.length === 0 ? (
                <MenuItem disabled>No areas available for this location</MenuItem>
              ) : (
                areas.map((area) => (
                  <MenuItem key={area.value} value={area.value}>
                    {area.label}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        )}

        <TextField
          fullWidth
          label="Remarks"
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          multiline
          rows={3}
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={isAccept ? handleApprove : handleReject}
          color={isAccept ? "success" : "error"}
          variant="contained"
          disabled={loading || (isAccept && (!selectedArea || areaLoading))}
          startIcon={loading && <CircularProgress size={20} color="inherit" />}
        >
          {isAccept ? "Approve" : "Reject"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StoreAssetTransferAction;
